/**
 * SeletorFases — régua de fases com lente arrastável (Produção v2).
 *
 * Porte fiel do componente canônico aprovado pela gestora
 * ("Componente - Seletor de Fases (PharuERP).html", versão SLIM, estilo
 * Apple Sports). Substitui qualquer seletor de fase anterior — não empilhar
 * com tabchips antigos.
 *
 * Comportamento (aprovado, não mudar):
 *   • Clique numa fase (rótulo OU trilho) -> a lente vai até ela (snap).
 *   • Arraste a lente -> move o range 1:1 com o cursor (transition: none
 *     durante o pointermove — easing na posição = "corre atrás").
 *   • Puxe as pontas ‹ › (zona de pega de 20px) -> estende/encolhe o range.
 *   • Ao PEGAR a lente levanta (sombra); ao SOLTAR, snap com
 *     cubic-bezier(.22,1,.36,1) — transição CSS SÓ no pointerup.
 *   • Pointer Events + setPointerCapture + touch-action:none (toque ok).
 *
 * API controlada:
 *   • `fases`   [{codigo, label}] — default = fases operacionais canônicas
 *     com os rótulos da gestora (brief 0.6/F.1 — NÃO renomear; F7/F8 não
 *     existem no vocabulário visível).
 *   • `value`   {start, end} — índices da seleção (inclusive).
 *   • `onChange(start, end)` — disparado no soltar/clique/teclado.
 *   • "Visão geral" fica à parte, como atalho que abre a lente para o
 *     range completo.
 *
 * Acessibilidade: trilho com role="slider" + setas ←/→ (movem o range fase
 * a fase), Home/End (primeira/última fase).
 *
 * Mapa de cores mockup -> tokens do tema (dark mode automático):
 *   #5B5BF6 accent · #EEF0F6 surface-2 (trilho) · #FFFFFF surface (lente)
 *   rgba(91,91,246,.22) accent-ring (borda da lente) · #C2C7D6 border-strong
 *   #8A92A6/#64748B ink-3 · border-radius 999px = radius-pill.
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import styled from "styled-components";

export const FASES_OPERACIONAIS = [
  { codigo: "F1", label: "F1 Definições" },
  { codigo: "F2", label: "F2 Projeto Exec." },
  { codigo: "F3", label: "F3 Otimização" },
  { codigo: "F4", label: "F4 Compras" },
  { codigo: "F5", label: "F5 Industrialização" },
  { codigo: "F6", label: "F6 Marcenaria" },
];

// Constantes travadas no componente aprovado.
const PAD = 7; // padding horizontal interno do trilho (px)
const HIT = 20; // zona de pega dos handles nas pontas da lente (px)
const SNAP_EASING = "cubic-bezier(.22,1,.36,1)"; // easing do encaixe (pointerup)

const Wrap = styled.div`
  width: 100%;
  min-width: 0;
`;

const Legenda = styled.div`
  font-size: 11px;
  color: var(--ink-3);
  text-align: center;
  margin: 0 0 8px;
`;

const Linha = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
`;

/* "Visão geral" à parte, como atalho — alinhado à altura do trilho para
   não desalinhar os rótulos das colunas de fase. */
const AtalhoVisaoGeral = styled.button`
  flex-shrink: 0;
  height: 40px;
  padding: 0 14px;
  border: none;
  border-radius: var(--radius-pill, 999px);
  background: ${({ $ativo }) =>
    $ativo ? "var(--accent-soft)" : "var(--surface-2)"};
  color: ${({ $ativo }) => ($ativo ? "var(--accent)" : "var(--ink-3)")};
  font-family: inherit;
  font-size: 11px;
  font-weight: ${({ $ativo }) => ($ativo ? 600 : 400)};
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.22s, background 0.22s;

  &:hover {
    color: var(--accent);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const Coluna = styled.div`
  flex: 1;
  min-width: 0;
`;

const Rotulos = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 7px;
`;

const Rotulo = styled.span`
  flex: 1;
  text-align: center;
  font-size: 11px;
  color: ${({ $in }) => ($in ? "var(--accent)" : "var(--ink-3)")};
  font-weight: ${({ $in }) => ($in ? 600 : 400)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.22s;
  cursor: pointer;
`;

const Trilho = styled.div`
  position: relative;
  display: flex;
  background: var(--surface-2);
  border-radius: var(--radius-pill, 999px);
  padding: 0 ${PAD}px;
  height: 40px;
  touch-action: none;
  user-select: none;
  cursor: ${({ $grab }) => ($grab ? "grabbing" : "pointer")};

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const Segmento = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  z-index: 2;
  pointer-events: none;

  i {
    display: block;
    height: 2.5px;
    border-radius: 3px;
    background: ${({ $in }) =>
      $in ? "var(--accent)" : "var(--border-strong)"};
    transition: background 0.22s;
  }
  i:nth-child(1) {
    width: 20px;
  }
  i:nth-child(2) {
    width: 14px;
  }
  i:nth-child(3) {
    width: 9px;
  }
`;

const Lente = styled.div`
  position: absolute;
  top: 4px;
  bottom: 4px;
  background: var(--surface);
  border: 1px solid var(--accent-ring);
  border-radius: var(--radius-pill, 999px);
  box-shadow: ${({ $lift }) => ($lift ? "var(--shadow-3)" : "var(--shadow-2)")};
  z-index: 1;
  transition: none;
`;

const Chevron = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
  z-index: 3;
  pointer-events: none;

  &.l {
    left: 2px;
  }
  &.r {
    right: 2px;
  }
`;

function SeletorFases({ fases = FASES_OPERACIONAIS, value, onChange }) {
  const N = fases.length;
  const clampIdx = (i) => Math.max(0, Math.min(N - 1, Number(i) || 0));
  const start = clampIdx(value?.start ?? 0);
  const end = Math.max(start, clampIdx(value?.end ?? N - 1));

  const trilhoRef = useRef(null);
  const lenteRef = useRef(null);
  // Estado mutável do arraste em curso (mode L | R | move | outside).
  const dragRef = useRef(null);
  // true -> a próxima reposição via layout effect anima (snap do pointerup,
  // clique em rótulo, teclado). Reposição por resize NÃO anima.
  const snapRef = useRef(false);

  const [levantada, setLevantada] = useState(false); // lift da lente
  const [arrastando, setArrastando] = useState(false); // cursor grabbing
  const [aoVivo, setAoVivo] = useState(null); // {ps, pe} durante o arraste
  const [larguraTrilho, setLarguraTrilho] = useState(0);

  // Range destacado (durante o arraste segue o cursor; fora dele, o value).
  const hs = aoVivo ? aoVivo.ps : start;
  const he = aoVivo ? aoVivo.pe : end;

  const segW = useCallback(() => {
    const w = trilhoRef.current?.clientWidth || 0;
    return (w - 2 * PAD) / N;
  }, [N]);

  const segAt = useCallback(
    (x) => Math.max(0, Math.min(N - 1, Math.floor((x - PAD) / segW()))),
    [N, segW],
  );

  // Posiciona a lente por manipulação direta de style (fidelidade 1:1 do
  // aprovado — sem re-render React por frame de arraste).
  const posicionar = useCallback(
    (ps, pe, anim) => {
      const trilho = trilhoRef.current;
      const lente = lenteRef.current;
      if (!trilho || !lente || !trilho.clientWidth) return;
      const w = (trilho.clientWidth - 2 * PAD) / N;
      lente.style.transition = anim
        ? `left .24s ${SNAP_EASING}, width .24s ${SNAP_EASING}`
        : "none";
      lente.style.left = `${PAD + ps * w}px`;
      lente.style.width = `${(pe - ps + 1) * w}px`;
    },
    [N],
  );

  // Reposiciona quando o value muda (anima só quando snapRef pediu) e
  // quando o trilho é medido/redimensionado (sem animação).
  useLayoutEffect(() => {
    posicionar(start, end, snapRef.current);
    snapRef.current = false;
  }, [start, end, larguraTrilho, posicionar]);

  useEffect(() => {
    const trilho = trilhoRef.current;
    if (!trilho || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      setLarguraTrilho(trilho.clientWidth);
    });
    ro.observe(trilho);
    return () => ro.disconnect();
  }, []);

  const selecionar = useCallback(
    (ns, ne) => {
      snapRef.current = true;
      if (ns !== start || ne !== end) onChange?.(ns, ne);
    },
    [start, end, onChange],
  );

  const onPointerDown = (e) => {
    const trilho = trilhoRef.current;
    if (!trilho) return;
    const r = trilho.getBoundingClientRect();
    const x = e.clientX - r.left;
    const w = segW();
    const lL = PAD + start * w;
    const lR = PAD + (end + 1) * w;
    let mode;
    if (x >= lL - HIT && x <= lL + HIT) mode = "L";
    else if (x >= lR - HIT && x <= lR + HIT) mode = "R";
    else if (x > lL && x < lR) mode = "move";
    else mode = "outside";

    dragRef.current = {
      mode,
      startX: x,
      oL: start,
      oR: end,
      livePS: start,
      livePE: end,
      moved: false,
    };
    trilho.setPointerCapture(e.pointerId);
    if (mode !== "outside") setLevantada(true);
    setArrastando(true);
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    const trilho = trilhoRef.current;
    const lente = lenteRef.current;
    if (!d || !d.mode || !trilho || !lente) return;
    const r = trilho.getBoundingClientRect();
    const x = e.clientX - r.left;
    const w = segW();
    if (Math.abs(x - d.startX) > 4) d.moved = true;
    if (!d.moved || d.mode === "outside") return;

    let leftPx;
    let widthPx;
    let ps = start;
    let pe = end;
    if (d.mode === "move") {
      const dx = x - d.startX;
      const span = d.oR - d.oL;
      leftPx = PAD + d.oL * w + dx;
      const maxL = PAD + (N - (span + 1)) * w;
      leftPx = Math.max(PAD, Math.min(maxL, leftPx));
      widthPx = (span + 1) * w;
      ps = Math.round((leftPx - PAD) / w);
      pe = ps + span;
    } else if (d.mode === "L") {
      leftPx = Math.max(PAD, Math.min(x, PAD + end * w));
      widthPx = PAD + (end + 1) * w - leftPx;
      ps = Math.round((leftPx - PAD) / w);
      if (ps > end) ps = end;
      pe = end;
    } else {
      const rightPx = Math.min(PAD + N * w, Math.max(x, PAD + (start + 1) * w));
      leftPx = PAD + start * w;
      widthPx = rightPx - leftPx;
      pe = Math.round((rightPx - PAD) / w) - 1;
      if (pe < start) pe = start;
      ps = start;
    }

    // Posição 1:1 com o cursor: transição desligada durante o pointermove.
    lente.style.transition = "none";
    lente.style.left = `${leftPx}px`;
    lente.style.width = `${Math.max(w, widthPx)}px`;
    d.livePS = ps;
    d.livePE = pe;
    setAoVivo((prev) =>
      prev && prev.ps === ps && prev.pe === pe ? prev : { ps, pe },
    );
  };

  const finalizar = () => {
    const d = dragRef.current;
    if (!d || !d.mode) return;
    let ns;
    let ne;
    if (!d.moved) {
      // Clique seco (rótulo do trilho ou fora da lente) -> vai até a fase.
      const idx = segAt(d.startX);
      ns = idx;
      ne = idx;
    } else {
      ns = d.livePS;
      ne = d.livePE;
    }
    dragRef.current = null;
    setAoVivo(null);
    setLevantada(false);
    setArrastando(false);
    // Snap imediato mesmo quando o value não muda (lente volta ao encaixe).
    posicionar(ns, ne, true);
    selecionar(ns, ne);
  };

  const onKeyDown = (e) => {
    let ns = start;
    let ne = end;
    if (e.key === "ArrowLeft") {
      if (start === 0) return;
      ns = start - 1;
      ne = end - 1;
    } else if (e.key === "ArrowRight") {
      if (end === N - 1) return;
      ns = start + 1;
      ne = end + 1;
    } else if (e.key === "Home") {
      ns = 0;
      ne = 0;
    } else if (e.key === "End") {
      ns = N - 1;
      ne = N - 1;
    } else {
      return;
    }
    e.preventDefault();
    selecionar(ns, ne);
  };

  const tudo = hs === 0 && he === N - 1;
  // Nome curto = primeira palavra do rótulo ("F2 Projeto Exec." -> "F2"),
  // como no componente aprovado — decoupla do `codigo` (que no P1 passou a
  // carregar o enum do backend, ex.: PROJETO_EXECUTIVO).
  const nomeCurtoDe = (fase) => (fase.label || "").split(" ")[0] || fase.codigo;
  const legenda = tudo
    ? "mostrando todas as fases"
    : hs === he
      ? `fase: ${fases[hs].label}`
      : `${nomeCurtoDe(fases[hs])} → ${nomeCurtoDe(fases[he])}  ·  ${he - hs + 1} fases`;

  return (
    <Wrap>
      <Legenda title="clique numa fase para ir até ela · arraste a lente ou puxe as setas para abranger várias">
        {legenda}
      </Legenda>
      <Linha>
        <AtalhoVisaoGeral
          type="button"
          $ativo={tudo}
          onClick={() => selecionar(0, N - 1)}
          title="Mostrar todas as fases"
        >
          Visão geral
        </AtalhoVisaoGeral>
        <Coluna>
          <Rotulos>
            {fases.map((fase, idx) => (
              <Rotulo
                key={fase.codigo}
                $in={idx >= hs && idx <= he}
                title={fase.label}
                onClick={() => selecionar(idx, idx)}
              >
                {fase.label}
              </Rotulo>
            ))}
          </Rotulos>
          <Trilho
            ref={trilhoRef}
            $grab={arrastando}
            role="slider"
            tabIndex={0}
            aria-label="Filtro de fases da produção"
            aria-valuemin={1}
            aria-valuemax={N}
            aria-valuenow={hs + 1}
            aria-valuetext={legenda}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={finalizar}
            onPointerCancel={finalizar}
            onKeyDown={onKeyDown}
          >
            {fases.map((fase, idx) => (
              <Segmento key={fase.codigo} $in={idx >= hs && idx <= he}>
                <i />
                <i />
                <i />
              </Segmento>
            ))}
            <Lente ref={lenteRef} $lift={levantada}>
              <Chevron className="l" aria-hidden="true">
                <FiChevronLeft size={14} />
              </Chevron>
              <Chevron className="r" aria-hidden="true">
                <FiChevronRight size={14} />
              </Chevron>
            </Lente>
          </Trilho>
        </Coluna>
      </Linha>
    </Wrap>
  );
}

export default SeletorFases;
